import moment, { Moment } from "moment-timezone";
import React, { Component, useContext, useEffect, useState } from "react";
import IOrder from "../../../models/IOrder";
import IShipment_GroupUI from "../../../models/IShipment_GroupUI";
import ISupplierModel from "../../../models/ISupplierModel";
import PriceUpchargeTypeEnum from "../../../models/PriceUpchargeTypeEnum";
import QBOInvoiceDateFieldEnum from "../../../models/QBOInvoiceDateFieldEnum";
import OwButton from "../OwButton/OwButton";
import OwDateInput from "../OwDateInput/OwDateInput";
import OwInputWrapper from "../OwInputWrapper/OwInputWrapper";
import OwLabel from "../OwLabel/OwLabel";
import OwTitle from "../OwTitle/OwTitle";
import IErrorDTO from "../../../models/DTO/IErrorDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IOnGetShipmentsForOrderDTO from "../../../models/DTO/IOnGetShipmentsForOrderDTO";
import IGetShipmentsForOrderDTO from "../../../models/DTO/IGetShipmentsForOrderDTO";
import IUser from "../../../models/IUser";
import { NumericFormat } from "react-number-format";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import { Socket } from "socket.io-client";
import { OwFnInput } from "../OwFnInput/OwFnInput";

type Props = {
    order?: IOrder;
    shipmentGroup: IShipment_GroupUI;
    saveFn: (shipmentGroup: IShipment_GroupUI, invoiceDate: string, shipVia: string, trackingNo: string, shipDate: string) => void;
    cancelFn: (refresh?: boolean) => void;
    notifyFn: (data: IErrorDTO | null, _notification?: any, isFormatted?: boolean) => void;
    socket: Socket;
};

interface ShippingCostBreakdown {
    shipments: { cost: number; tracking_no: string; guid: string }[];
    markup: { total: number; type: PriceUpchargeTypeEnum; amount: number };
}

// Order date fields that hold a real timestamp; the rest are picked calendar dates (mirrors the API's
// QuickbooksOnlineService.QBO_TIMESTAMP_DATE_FIELDS).
const QBO_TIMESTAMP_DATE_FIELDS: QBOInvoiceDateFieldEnum[] = [QBOInvoiceDateFieldEnum.SUBMIT_DATE, QBOInvoiceDateFieldEnum.CONFIRMED_DATE];

const orderDateFieldToMoment = (order: IOrder, field: QBOInvoiceDateFieldEnum): Moment => {
    const value = order[field];

    if (!value || QBO_TIMESTAMP_DATE_FIELDS.includes(field)) {
        return moment(value);
    }

    // Calendar dates are stored at 00:00 UTC or the picker's local midnight, so their UTC date is the
    // picked date. Reading them in local time would show the previous day for US users.
    const utcDate = moment.utc(value);
    return utcDate.isValid() ? moment(utcDate.format("YYYY-MM-DD"), "YYYY-MM-DD") : utcDate;
};

const getQuickBooksOnlineInvoiceAndShipDateField = (
    supplierModel: ISupplierModel,
    order?: IOrder
): { dateField: Moment; shipDateField: Moment } => {
    let dateField = moment();

    let shipDateField = moment();

    if (supplierModel.Supplier_Integration.qbo_invoice_default_date && order) {
        dateField = orderDateFieldToMoment(order, supplierModel.Supplier_Integration.qbo_invoice_default_date);
    }

    if (supplierModel.Supplier_Integration.qbo_invoice_default_ship_date && order) {
        shipDateField = orderDateFieldToMoment(order, supplierModel.Supplier_Integration.qbo_invoice_default_ship_date);
    }

    if (!dateField.isValid()) {
        dateField = moment();
    }

    if (!shipDateField.isValid()) {
        shipDateField = moment();
    }

    return { dateField, shipDateField };
};

// export const AccountsHeader: React.FC<Props> = (props) => {
export const QuickBooksOnlineInvoiceSidebar: React.FC<Props> = (props) => {
    const userModel = useContext(UserContext);
    const supplierModel = useContext(SupplierContext);

    const { dateField, shipDateField } = getQuickBooksOnlineInvoiceAndShipDateField(supplierModel, props.order);

    const [shippingCost, setShippingCost] = useState(0);
    const [hasShippingCost, setHasShippingCost] = useState(true); // TODO is this necessary?
    const [selectedDate, setSelectedDate] = useState<Moment>(
        props.shipmentGroup.created_at ? moment(props.shipmentGroup.created_at) : dateField
    );
    const [shipVia, setShipVia] = useState("");
    const [trackingNo, setTrackingNo] = useState("");
    const [shipDate, setShipDate] = useState<Moment>(shipDateField);
    const [active, setActive] = useState(false);
    const [shippingCostBreakdown, setShippingCostBreakdown] = useState<ShippingCostBreakdown>();
    const [showShippingCostBreakdown, setShowShippingCostBreakdown] = useState(false);
    const [hasOrderShippingCosts, setHasOrderShippingCosts] = useState(false);

    useEffect(() => {
        const { _hasOrderShippingCosts, _shippingCostBreakdown, _shippingCost, _trackingNo, _shipVia } = getStateDetails();

        setShippingCost(_shippingCost);
        setShippingCostBreakdown(_shippingCostBreakdown);
        setTrackingNo(_trackingNo);
        setShipVia(_shipVia);
        setHasOrderShippingCosts(_hasOrderShippingCosts);

        if (!_shippingCost) {
            getShipmentsForOrder();
        }

        props.socket.on(EndpointsEnum.ShipmentOnGetShipmentsForOrder, onGetShipmentsForOrder);

        return () => {
            props.socket.removeListener(EndpointsEnum.ShipmentOnGetShipmentsForOrder, onGetShipmentsForOrder);
        };
    }, []);

    const getOrdersShippingCosts = (orders: IOrder[]) => {};

    const getStateDetails = (): {
        _hasOrderShippingCosts: boolean;
        _shippingCost: number;
        _shippingCostBreakdown: ShippingCostBreakdown;
        _trackingNo: string;
        _shipVia: string;
    } => {
        let _shippingCost = 0;
        const _trackingNo: string[] = [];
        const _shipViaSet: Set<string> = new Set();
        let _shipVia: string = "";

        // TODO - move this to a service and unit test it.
        const _shippingCostBreakdown: ShippingCostBreakdown = {
            shipments: [],
            markup: {
                total: 0,
                type: PriceUpchargeTypeEnum.PERCENT,
                amount: 0,
            },
        };

        let _hasOrderShippingCosts = false;

        if (props.order) {
            if (props.order.shipping) {
                _hasOrderShippingCosts = true;

                _shippingCost = props.order.shipping;
            }

            if (props.order.Shipments?.length) {
                for (const shipment of props.order.Shipments) {
                    if (shipment.shipment.Shipping_Carrier?.name && shipment.shipment.Shipping_Carrier?.name.length) {
                        _shipViaSet.add(shipment.shipment.Shipping_Carrier?.name);
                        _trackingNo.push(shipment.shipment.tracking);
                    }
                }
            } else {
                _shipViaSet.add(props.order.qbo_ShipMethodRefValue || "");
            }
        }

        if (props.shipmentGroup) {
            if (props.shipmentGroup.Orders) {
                for (const order of props.shipmentGroup.Orders) {
                    if (order.shipping) {
                        _shippingCost += order.shipping || 0;
                        _hasOrderShippingCosts = true;
                    }
                }
            }

            if (props.shipmentGroup.Shipments) {
                for (const shipment of props.shipmentGroup.Shipments) {
                    if (!shipment.is_voided) {
                        if (shipment.tracking && shipment.tracking.length) {
                            _trackingNo.push(shipment.tracking);
                        }
                        if (shipment.Shipping_Carrier?.name && shipment.Shipping_Carrier?.name.length) {
                            _shipViaSet.add(shipment.Shipping_Carrier?.name);
                        }

                        if (!_hasOrderShippingCosts) {
                            _shippingCost += shipment.total_cost || 0;
                        }

                        _shippingCostBreakdown.shipments.push({
                            cost: shipment.total_cost || 0,
                            guid: shipment.guid,
                            tracking_no: shipment.tracking,
                        });
                    }
                }
            }
        }

        if (supplierModel.has_shipping_fee && !_hasOrderShippingCosts) {
            switch (supplierModel.shipment_cost_price_upcharge_type_id) {
                case PriceUpchargeTypeEnum.FIXED: {
                    const markup = Number(supplierModel.shipment_cost_markup || 0);
                    _shippingCost += markup;
                    _shippingCostBreakdown.markup = {
                        total: markup,
                        amount: markup,
                        type: PriceUpchargeTypeEnum.FIXED,
                    };
                    break;
                }

                case PriceUpchargeTypeEnum.PERCENT: {
                    const percent = Number(supplierModel.shipment_cost_markup || 0);
                    const totalMarkup = _shippingCost * (percent / 100);
                    _shippingCost += totalMarkup;
                    _shippingCostBreakdown.markup = {
                        total: totalMarkup,
                        amount: percent,
                        type: PriceUpchargeTypeEnum.PERCENT,
                    };
                    break;
                }
            }
        }

        _shippingCost = Number(_shippingCost.toFixed(2));

        _shipVia = Array.from(_shipViaSet).join(", ");

        return { _hasOrderShippingCosts, _shippingCost, _shippingCostBreakdown, _trackingNo: _trackingNo.join(", "), _shipVia };
    };

    const getShipmentsForOrder = () => {
        if (props.order) {
            const payload: IGetShipmentsForOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                orderGuid: props.order.guid,
            };

            props.socket.emit(EndpointsEnum.ShipmentGetShipmentsForOrder, payload);

            setActive(true);
        }
    };

    const onGetShipmentsForOrder = (data: IOnGetShipmentsForOrderDTO) => {
        const trackingArr = data.shipments.map((mapItem) => mapItem.tracking);

        let _shippingCost = 0;

        const _shippingCostBreakdown: ShippingCostBreakdown = {
            shipments: [],
            markup: { total: 0, type: PriceUpchargeTypeEnum.PERCENT, amount: 0 },
        };

        for (const shipment of data.shipments) {
            if (!shipment.is_voided) {
                const price = Number(shipment.shipment_cost || 0);

                _shippingCost += price;

                _shippingCostBreakdown.shipments.push({ cost: price, guid: shipment.guid, tracking_no: shipment.tracking });
            }
        }

        if (supplierModel.shipment_cost_markup) {
            switch (supplierModel.shipment_cost_price_upcharge_type_id) {
                case PriceUpchargeTypeEnum.FIXED: {
                    const markup = Number(supplierModel.shipment_cost_markup || 0);
                    _shippingCost += markup;

                    _shippingCostBreakdown.markup = {
                        total: markup,
                        amount: markup,
                        type: PriceUpchargeTypeEnum.FIXED,
                    };
                    break;
                }

                case PriceUpchargeTypeEnum.PERCENT: {
                    const percent = Number(supplierModel.shipment_cost_markup || 0);
                    const totalMarkup = shippingCost * (percent / 100);
                    _shippingCost += totalMarkup;

                    _shippingCostBreakdown.markup = {
                        total: totalMarkup,
                        amount: percent,
                        type: PriceUpchargeTypeEnum.PERCENT,
                    };
                    break;
                }
            }
        }

        setActive(false);

        setTrackingNo(trackingArr.join(", "));
        setShippingCost(_shippingCost);
        setActive(false);
        setShippingCostBreakdown(_shippingCostBreakdown);
    };

    const saveInvoice = () => {
        if (hasShippingCost && selectedDate) {
            const shipment = props.shipmentGroup;

            if (hasShippingCost) {
                shipment.shipping_cost = shippingCost;
            }

            const invoiceDate = selectedDate.format("YYYY/MM/DD");
            const _shipDate = shipDate.format("YYYY/MM/DD");

            // console.log(shipment, invoiceDate, shipVia, trackingNo, _shipDate);

            props.saveFn(shipment, invoiceDate, shipVia, trackingNo, _shipDate);

            setActive(true);
        }
    };

    const cancel = () => {
        props.cancelFn();
    };

    const handleChange = (e: any) => {
        const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        const name = e.target.name;

        const state: any = {
            [name]: value,
        };

        switch (name) {
            case "shippingCost":
                setShippingCost(value);
                break;

            case "shipVia":
                setShipVia(value);
                break;

            case "trackingNo":
                setTrackingNo(value);
                break;
        }
    };

    const handleDateChange = (detail: Moment) => {
        setSelectedDate(detail);
    };

    const handleShipDateChange = (detail: Moment) => {
        setShipDate(detail);
    };

    const toggleViewShippingCostBreakdown = () => {
        setShowShippingCostBreakdown(!showShippingCostBreakdown);
    };

    return (
        <div className="InvoiceSidebar p-3" style={{ height: "100vh", overflow: "hidden" }}>
            <div style={{ height: "15vh" }}>
                <div className="text-end">
                    <OwButton
                        centerIconClass={"far fa-times"}
                        fn={() => {
                            props.cancelFn();
                        }}
                        className={"btn-danger"}
                    />
                </div>
                <OwTitle leftIconName={"fas fa-file-alt"} title={"invoice"} />
                {/*<h2><i className="fas fa-truck"></i> {header}</h2>*/}
                <hr />
            </div>

            <div className="review-section pe-3" style={{ height: "85vh", overflowY: "scroll" }}>
                <div className={"mb-2"}>
                    <OwLabel title={"invoice_date"} />
                    <OwDateInput dateFormat={"MM-dd-yyyy"} fn={handleDateChange} selectedDate={selectedDate} />
                </div>

                <div className={"mb-2"}>
                    <OwLabel title={"shipping_cost"} />
                    <OwFnInput
                        value={shippingCost}
                        name={"shippingCost"}
                        // disabled={!hasShippingCost}
                        type={"text"}
                        isSmall={true}
                        fn={handleChange}
                    />
                </div>

                {hasShippingCost ? (
                    <div className={"mb-2"}>
                        <div>
                            <OwLabel title={"shipping_cost_breakdown"} />
                            <OwLabel
                                className={"ms-3"}
                                title={showShippingCostBreakdown ? "hide" : "view"}
                                fn={toggleViewShippingCostBreakdown}
                            />
                        </div>
                        {showShippingCostBreakdown ? (
                            <div>
                                {hasOrderShippingCosts ? (
                                    <div>
                                        <p>
                                            <OwLabel
                                                leftIconClass={"fas fa-exclamation-triangle"}
                                                className={"text-warning"}
                                                title={"has_order_shipping_costs_actual_shipment_costs_ignored"}
                                            />
                                        </p>
                                    </div>
                                ) : null}
                                <div className="d-flex">
                                    <div className="w-50">
                                        <OwLabel title={"detail"} isBold={true} isUnderline={true} />
                                    </div>
                                    <div className="w-50 text-end">
                                        <OwLabel title={"amount"} isBold={true} isUnderline={true} />
                                    </div>
                                </div>
                                {shippingCostBreakdown &&
                                    shippingCostBreakdown.shipments.map((shipmentCost, index) => {
                                        return (
                                            <div key={shipmentCost.guid} className={"d-flex"}>
                                                <div className="w-50">
                                                    <div>
                                                        <OwLabel title={"shipment"} /> {index + 1}
                                                    </div>
                                                    <div className={"text-small text-muted"}>{shipmentCost.tracking_no}</div>
                                                </div>
                                                <div className="w-50 text-end">
                                                    <NumericFormat
                                                        value={shipmentCost.cost}
                                                        displayType={"text"}
                                                        fixedDecimalScale={true}
                                                        decimalScale={2}
                                                        thousandSeparator={true}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                {shippingCostBreakdown && shippingCostBreakdown.markup.total ? (
                                    <div className={"d-flex"}>
                                        <div className="w-50">
                                            <OwLabel title={"markup"} /> ({shippingCostBreakdown.markup.amount}
                                            {shippingCostBreakdown.markup.type === PriceUpchargeTypeEnum.PERCENT ? "%" : ""})
                                        </div>
                                        <div className="w-50 text-end">
                                            <NumericFormat
                                                value={shippingCostBreakdown.markup.total}
                                                displayType={"text"}
                                                fixedDecimalScale={true}
                                                decimalScale={2}
                                                thousandSeparator={true}
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ) : null}

                <div className={"mb-2"}>
                    <OwLabel title={"ship_via"} />
                    <OwInputWrapper name={"shipVia"} value={shipVia} fn={handleChange} />
                </div>

                <div className={"mb-2"}>
                    <OwLabel title={"tracking_no"} />
                    <OwInputWrapper name={"trackingNo"} value={trackingNo} fn={handleChange} />
                </div>

                <div className={"mb-2"}>
                    <OwLabel title={"ship_date"} />
                    <OwDateInput dateFormat={"MM-dd-yyyy"} fn={handleShipDateChange} selectedDate={shipDate} />
                </div>

                <div>
                    <OwButton className={"btn-primary me-2"} title={"save"} fn={saveInvoice} active={active} disableOnActive={true} />
                    <OwButton className={"btn-danger"} title={"cancel"} fn={cancel} />
                </div>
            </div>
        </div>
    );
};
